import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import classNames from 'classnames';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers.js';
import { requireListingImage } from '../../util/configHelpers';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  isDownloadProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';

// Shared components
import {
  H4,
  H3,
  Page,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  Heading,
  IconHeart,
  ExternalLink,
} from '../../components';

// Related components and modules
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  priceForSchemaMaybe,
} from './ListingPage.shared';
import SectionTextMaybe from './SectionTextMaybe';
import SectionEventDateMaybe from './SectionEventDateMaybe';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionMapMaybe from './SectionMapMaybe';
import SectionImageGrid from './SectionImageGrid/SectionImageGrid';
import SectionAuthorCustom from './SectionAuthorCustom/SectionAuthorCustom';
import ListingAddressText from './ListingAddressText/ListingAddressText';
import ActionBarMaybe from './ActionBarMaybe.js';
import ProducerTypeWithIcons from './ProducerTypeWithIcons/ProducerTypeWithIcons.js';
import AddToFavoriteButton from '../../extensions/Favorites/components/AddFavoriteButton/AddToFavoriteButton.js';
import SectionReviews from './SectionReviews';
import CustomListingFields from './CustomListingFields';
import TypeWithIcons from '../../extensions/ViewListingPage/components/TypeWithIcons/TypeWithIcons.js';
import FeaturesWithIcons from '../../extensions/ViewListingPage/components/FeaturesWithIcons/FeaturesWithIcons.js';
import MarketsEventsWithIcons from '../../extensions/ViewListingPage/components/MarketsEventsWithIcons/MarketsEventsWithIcons.js';
import ProducersPlacesWithIcons from '../../extensions/ViewListingPage/components/ProducersPlacesWithIcons/ProducersPlacesWithIcons.js';
import PlaceTypeWithIcons from '../../extensions/ViewListingPage/components/PlaceTypeWithIcons/PlaceTypeWithIcons.js';
import FarmToForkDiningFeaturesWithIcons from '../../extensions/ViewListingPage/components/FarmToForkDiningFeaturesWithIcons/FarmToForkDiningFeaturesWithIcons.js';
import { EXPERIENCES } from '../../extensions/SearchPage/config/configCategories.js';
import { getCategoryPlaceholderSrc } from '../../config/placeholderImages';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const LISTING_FIELDS_TO_EXCLUDE = [
  'address_in_listing',
  'producer_type_category',
  'type',
  'markets_events',
  'producers_places',
  'features',
  'placetype',
  'farm_to_fork_dining_features',
];

// Fields specific to local producers that should be rendered explicitly.
// These fields have categoryConfig tied to removed subcategory 'premium-listing',
// so they get stripped from the merged config by validation. We hardcode labels here.
const LOCAL_PRODUCER_OPTIONAL_FIELDS = [
  { key: 'what_do_you_produce', label: 'What do you produce?' },
  { key: 'source_materials', label: 'Where do you source your ingredients / materials from?' },
  { key: 'why_love_your_products', label: 'Why do your customers love your products?' },
  { key: 'range_of_products', label: 'What are your best selling products?' },
  { key: 'where_buy_products', label: 'Where can we buy your products?' },
  { key: 'open_for_visitors', label: 'Are you open for visitors? If yes, what are your opening hours?' },
  { key: 'awards_recommendations', label: 'Have you received any awards / lovely recommendations from experts?' },
  { key: 'sell_wholesale', label: 'Do you sell wholesale? If so how do we get in touch with you?' },
  { key: 'best_local_producer', label: "Who's the best local producer near you (except you of course!)?" },
  { key: 'must_see_local_place', label: 'What\'s your "must see" local place?' },
];

const { UUID } = sdkTypes;

const prepareUrl = url => {
  if (typeof url !== 'string') return url;
  if (!url.startsWith('http')) {
    return `http://${url}`;
  }
  return url;
};

const convertToUrls = (urlsStr = '') => {
  const urlsList = urlsStr.split(/[\s,]+/);
  return urlsList.reduce((res, url) => {
    const preparedUrl = prepareUrl(url);
    res.push(
      <>
        <ExternalLink href={preparedUrl}>{url}</ExternalLink>
        <br />
      </>
    );
    return res;
  }, []);
};

const createFilterOptions = options => options.map(o => ({ key: `${o.option}`, label: o.label }));

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  const [imageCarouselOpen, setImageCarouselOpen] = useState(false);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews = [],
    fetchReviewsError,
    sendInquiryInProgress,
    sendInquiryError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    showOwnListingsOnly,
    ...restOfProps
  } = props;

  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isVariant = rawParams.variant != null;
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant || showOwnListingsOnly
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  // Digital downloads take payment too, so they must not fall through to 'inquiry'
  // here - that would suppress the "you have not added payout details" warning
  // on the owner's own listing. The microcopy renders it as "for purchase".
  const isDownload = isDownloadProcess(processName);
  const processType = isBooking ? 'booking' : isPurchase || isDownload ? 'purchase' : 'inquiry';

  const validListingTypes = listingConfig.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showListingImage = requireListingImage(foundListingTypeConfig);

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <div>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </div>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  const handleViewPhotosClick = e => {
    // Stop event from bubbling up to prevent image click handler
    // trying to open the carousel as well.
    e.stopPropagation();
    setImageCarouselOpen(true);
  };

  const actionBar =
    mounted && currentListing.id && isOwnListing ? (
      <>
        {noPayoutDetailsSetWithOwnListing ? (
          <ActionBarMaybe
            className={classNames(css.actionBarForHeroLayout, {
              [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
            })}
            isOwnListing={isOwnListing}
            listing={currentListing}
            showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
            currentUser={currentUser}
          />
        ) : null}
        <ActionBarMaybe
          className={classNames(css.actionBarForHeroLayout, {
            [css.actionBarNoBorderRadiusOnMobile]: !showListingImage,
          })}
          isOwnListing={isOwnListing}
          listing={currentListing}
          currentUser={currentUser}
          editParams={{
            id: listingId.uuid,
            slug: listingSlug,
            type: listingPathParamType,
            tab: listingTab,
          }}
        />
      </>
    ) : null;

  // PNB listings are editorial discovery entries for real-world producers and places,
  // not transactional product pages. Only emit Product/Offer structured data when a real
  // price exists. Otherwise use LocalBusiness so Google does not run merchant-listing
  // validation (which requires both `offers.price` and `image`) and flag every price-less
  // listing as invalid. Omit `image` entirely when there are no photos (empty array is invalid).
  const hasPrice = !!(price && price.amount);
  const schemaImageMaybe = schemaImages.length > 0 ? { image: schemaImages } : {};
  const listingAddress = publicData?.location?.address;
  const addressMaybe =
    typeof listingAddress === 'string' && listingAddress ? { address: listingAddress } : {};
  const geoMaybe = geolocation
    ? { geo: { '@type': 'GeoCoordinates', latitude: geolocation.lat, longitude: geolocation.lng } }
    : {};

  const pageSchema = hasPrice
    ? {
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: title,
        ...schemaImageMaybe,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...priceForSchemaMaybe(price),
          ...availabilityMaybe,
        },
      }
    : {
        '@context': 'http://schema.org',
        '@type': 'LocalBusiness',
        name: title,
        description: description,
        url: productURL,
        ...schemaImageMaybe,
        ...addressMaybe,
        ...geoMaybe,
      };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={pageSchema}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        {showListingImage ? (
          <SectionImageGrid
            actionBarMaybe={actionBar}
            images={currentListing.images}
            imageCarouselOpen={imageCarouselOpen}
            onImageCarouselClose={() => setImageCarouselOpen(false)}
            handleViewPhotosClick={handleViewPhotosClick}
            onManageDisableScrolling={onManageDisableScrolling}
            noPayoutDetailsSetWithOwnListing={noPayoutDetailsSetWithOwnListing}
            placeholderSrc={getCategoryPlaceholderSrc(publicData?.categoryLevel1)}
          />
        ) : isOwnListing ? (
          <div className={css.actionBarContainerForNoListingImage}>{actionBar}</div>
        ) : (
          <div className={css.actionBarContainerForNoListingImage} />
        )}
        <div
          className={classNames(css.contentWrapperForHeroLayout, {
            // SectionImageGrid already offsets itself below the fixed topbar,
            // so the wrapper must not add that offset a second time.
            [css.contentWrapperForHeroLayoutWithImages]: showListingImage,
          })}
        >
          <div className={css.mainColumnForHeroLayout}>
            <div className={css.authorContainer}>
              <SectionAuthorCustom author={ensuredAuthor} listing={currentListing} />
              <AddToFavoriteButton
                listing={currentListing}
                customHeartComponent={intl.formatMessage({ id: 'ListingPage.favorites' })}
                customHeartComponentClassName={css.customHeartClassName}
              />
            </div>
            {/* titleVisibleOnDesktop keeps the title visible on desktop as well: the
                cover-photo layout hides the OrderPanel heading, so this is the only
                place the listing title is rendered. */}
            {showListingImage ? (
              <div className={classNames(css.mobileHeading, css.titleVisibleOnDesktop)}>
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              </div>
            ) : (
              <div className={css.noListingImageHeadingHero}>
                <div className={classNames(css.mobileHeading, css.titleForCoverLayout)}>
                  <Heading styledAs="h2" as="h1" className={css.orderPanelTitle}>
                    <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                  </Heading>
                </div>
              </div>
            )}
            <ListingAddressText listing={currentListing} />
            <SectionTextMaybe text={description} showAsIngress />

            <ProducerTypeWithIcons listing={currentListing} listingConfig={listingConfig} />
            <TypeWithIcons listing={currentListing} listingConfig={listingConfig} />
            <MarketsEventsWithIcons listing={currentListing} listingConfig={listingConfig} />
            <ProducersPlacesWithIcons listing={currentListing} listingConfig={listingConfig} />
            <FeaturesWithIcons listing={currentListing} listingConfig={listingConfig} />
            <PlaceTypeWithIcons listing={currentListing} listingConfig={listingConfig} />
            <FarmToForkDiningFeaturesWithIcons
              listing={currentListing}
              listingConfig={listingConfig}
            />

            {listingConfig.listingFields.reduce((pickedElements, config) => {
              const { key, enumOptions, includeForListingTypes, scope = 'public' } = config;

              const { listingType, categoryLevel1 } = publicData || {};
              const isLocalProducers = categoryLevel1 === 'local-producers';

              // Exclude fields rendered explicitly elsewhere
              const fieldToExclude =
                categoryLevel1 !== EXPERIENCES
                  ? [...LISTING_FIELDS_TO_EXCLUDE, 'experience_type']
                  : [...LISTING_FIELDS_TO_EXCLUDE, 'produce_type'];
              // For local producers, skip fields rendered explicitly below
              if (fieldToExclude.includes(key)) {
                return pickedElements;
              }
              if (isLocalProducers && LOCAL_PRODUCER_OPTIONAL_FIELDS.some(f => f.key === key)) {
                return pickedElements;
              }

              const isTargetListingType =
                isLocalProducers ||
                includeForListingTypes == null ||
                includeForListingTypes.includes(listingType);

              const value =
                scope === 'public' ? publicData[key] : scope === 'metadata' ? metadata[key] : null;
              const hasValue = value != null;
              return isTargetListingType && config.schemaType === SCHEMA_TYPE_MULTI_ENUM
                ? [
                    ...pickedElements,
                    <SectionMultiEnumMaybe
                      key={key}
                      heading={config?.showConfig?.label}
                      options={createFilterOptions(enumOptions)}
                      selectedOptions={value || []}
                    />,
                  ]
                : isTargetListingType && hasValue && config.schemaType === SCHEMA_TYPE_TEXT
                ? [
                    ...pickedElements,
                    key === 'eventDate' || key === 'eventDatesArray' ? (
                      <SectionEventDateMaybe
                        key={key}
                        heading={config?.showConfig?.label}
                        eventDate={value}
                      />
                    ) : (
                      <SectionTextMaybe key={key} heading={config?.showConfig?.label} text={value} />
                    ),
                  ]
                : pickedElements;
            }, [])}

            {/* Explicit rendering of local producer optional fields.
                These fields had categoryConfig tied to removed subcategory 'premium-listing'.
                Rendered directly to bypass category matching — only shown when populated. */}
            {publicData?.categoryLevel1 === 'local-producers' &&
              LOCAL_PRODUCER_OPTIONAL_FIELDS.map(({ key, label }) => {
                const value = publicData[key];
                return (
                  <SectionTextMaybe key={key} heading={label} text={value} />
                );
              })}

            <SectionTextMaybe
              heading={intl.formatMessage({ id: 'EditListingDetailsForm.websiteLabel' })}
              text={convertToUrls(publicData.website_field)}
            />
            <SectionTextMaybe
              heading={intl.formatMessage({ id: 'EditListingDetailsForm.addressLabel' })}
              text={publicData.address_in_listing}
            />

            <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            />
            <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            />
          </div>
          <div className={css.orderColumnForHeroLayout}>
            <OrderPanel
              className={css.orderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name={isVariant ? 'ListingPageVariant' : 'ListingPage'}
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={<FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />}
              titleDesktop={
                <H4 as="h1" className={css.orderPanelTitle}>
                  <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
                </H4>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              {...restOfProps}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={config.stripe.dayCountAvailableForBooking}
              marketplaceName={config.marketplaceName}
              currentPage="ListingPageCoverPhoto"
              showListingImage={showListingImage}
            />
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

/**
 * The ListingPage component with cover photo layout
 *
 * @component
 * @param {Object} props
 * @param {Object} props.params - The path params object
 * @param {string} props.params.id - The listing id
 * @param {string} props.params.slug - The listing slug
 * @param {LISTING_PAGE_DRAFT_VARIANT | LISTING_PAGE_PENDING_APPROVAL_VARIANT} props.params.variant - The listing variant
 * @param {boolean} props.isAuthenticated - Whether the user is authenticated
 * @param {Object} props.currentUser - The current user
 * @param {Function} props.getListing - The get listing function
 * @param {Function} props.getOwnListing - The get own listing function
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @param {string} props.inquiryModalOpenForListingId - The inquiry modal open for the specific listing id
 * @param {propTypes.error} props.showListingError - The show listing error
 * @param {Function} props.callSetInitialValues - The call setInitialValues function, which is given to this function as a parameter
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.fetchReviewsError - The fetch reviews error
 * @param {Object<string, Object>} props.monthlyTimeSlots - The monthly time slots. E.g. { '2019-11': { timeSlots: [], fetchTimeSlotsInProgress: false, fetchTimeSlotsError: null } }
 * @param {Object<string, Object>} props.timeSlotsForDate - The time slots for date. E.g. { '2019-11-01': { timeSlots: [], fetchedAt: 1572566400000, fetchTimeSlotsError: null, fetchTimeSlotsInProgress: false } }
 * @param {boolean} props.sendInquiryInProgress - Whether the send inquiry is in progress
 * @param {propTypes.error} props.sendInquiryError - The send inquiry error
 * @param {Function} props.onSendInquiry - The on send inquiry function
 * @param {Function} props.onInitializeCardPaymentData - The on initialize card payment data function
 * @param {Function} props.onFetchTimeSlots - The on fetch time slots function
 * @param {Function} props.onFetchTransactionLineItems - The on fetch transaction line items function
 * @param {Array<propTypes.transactionLineItem>} props.lineItems - The line items
 * @param {boolean} props.fetchLineItemsInProgress - Whether the fetch line items is in progress
 * @param {propTypes.error} props.fetchLineItemsError - The fetch line items error

 * @returns {JSX.Element} listing page component
 */
const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant != null;
  const currentUser = props.currentUser;
  if (isForbiddenError(showListingError) && !isVariant && !currentUser) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRights = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(showListingError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (hasNoViewingRights && isForbiddenError(showListingError)) ||
    isErrorNoViewingPermission(showListingError)
  ) {
    // If the user has no viewing rights, fetching anything but their own listings
    // will return a 403 error. If that happens, redirect to NoAccessPage.
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      showOwnListingsOnly={hasNoViewingRights}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    monthlyTimeSlots,
    timeSlotsForDate,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    monthlyTimeSlots, // for OrderPanel
    timeSlotsForDate, // for OrderPanel
    lineItems, // for OrderPanel
    fetchLineItemsInProgress, // for OrderPanel
    fetchLineItemsError, // for OrderPanel
    sendInquiryInProgress,
    sendInquiryError,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)), // for OrderPanel
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone, options) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone, options)), // for OrderPanel
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
